import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { ScaffolderTaskStatus } from '@backstage/plugin-scaffolder-common';
import { DateTime } from 'luxon';

const TASK_STATUSES = [
  'open',
  'processing',
  'failed',
  'completed',
  'cancelled',
  'skipped',
] as const satisfies readonly ScaffolderTaskStatus[];

type ScaffolderTask = {
  id: string;
  spec?: {
    templateInfo?: {
      entityRef?: string;
      entity?: {
        metadata?: {
          name?: string;
          title?: string;
        };
      };
    };
  };
  status: ScaffolderTaskStatus;
  createdAt: string;
  createdBy?: string;
  lastHeartbeatAt?: string;
};

type ScaffolderTasksResponse = ScaffolderTask[] | { tasks: ScaffolderTask[] };

type TaskExplorerFilters = {
  createdBy?: string;
  status?: ScaffolderTaskStatus[];
  templateFilter?: string[];
  dateRange?: string;
};

type TaskExplorerResult = {
  tasks: ScaffolderTask[];
  totalTasks: number;
  isLoading: boolean;
  error?: Error;
  reload: () => void;
};

const getDateThreshold = (range?: string): DateTime | undefined => {
  if (!range) return undefined;
  const now = DateTime.now();
  switch (range) {
    case '24h':
      return now.minus({ hours: 24 });
    case '7d':
      return now.minus({ days: 7 });
    case '30d':
      return now.minus({ days: 30 });
    case '60d':
      return now.minus({ days: 60 });
    case '90d':
      return now.minus({ days: 90 });
    case '120d':
      return now.minus({ days: 120 });
    case '180d':
      return now.minus({ days: 180 });
    default:
      return undefined;
  }
};

const normalizeTasks = (data: ScaffolderTasksResponse): ScaffolderTask[] => {
  if (Array.isArray(data)) {
    return data;
  }
  return data.tasks ?? [];
};

export type { ScaffolderTask, TaskExplorerFilters };
export { TASK_STATUSES };

export const useTaskExplorer = (
  filters: TaskExplorerFilters,
): TaskExplorerResult => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<ScaffolderTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const reload = useCallback(() => {
    setReloadTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchTasks = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const params = new URLSearchParams();

        if (filters.createdBy) {
          params.append('createdBy', filters.createdBy);
        }

        if (filters.status && filters.status.length > 0) {
          for (const status of filters.status) {
            params.append('status', status);
          }
        }

        const response = await fetchApi.fetch(
          `${baseUrl}/v2/tasks?${params.toString()}`,
          { signal: abortController.signal },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch tasks: ${response.status} ${response.statusText}`,
          );
        }

        const json = await response.json();
        setData(normalizeTasks(json));
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();

    return () => {
      abortController.abort();
    };
  }, [
    discoveryApi,
    fetchApi,
    filters.createdBy,
    filters.status,
    reloadTrigger,
  ]);

  const filteredTasks = useMemo(() => {
    let result = data;

    if (filters.templateFilter && filters.templateFilter.length > 0) {
      const filterRefs = new Set(
        filters.templateFilter.map(ref => ref.toLowerCase()),
      );
      result = result.filter(task => {
        const templateRef =
          task.spec?.templateInfo?.entityRef?.toLowerCase() ?? '';
        return filterRefs.has(templateRef);
      });
    }

    const threshold = getDateThreshold(filters.dateRange);
    if (threshold) {
      result = result.filter(
        task => DateTime.fromISO(task.createdAt) >= threshold,
      );
    }

    return result;
  }, [data, filters.templateFilter, filters.dateRange]);

  return {
    tasks: filteredTasks,
    totalTasks: filteredTasks.length,
    isLoading,
    error,
    reload,
  };
};
